// Mini-agenda widget voor de Mail Hub sidebar
// - Compact maand-overzicht (dagen met events krijgen een puntje)
// - Lijst van komende 5 events
// - Klik op dag of event → opent volledige /agenda pagina
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, format, parseISO,
  isSameDay, isSameMonth, isAfter,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { CaretLeft, CaretRight, CalendarBlank, MapPin } from '@phosphor-icons/react';

const safeParse = (s) => { try { return parseISO(s); } catch { return new Date(s); } };

export default function MailSidebarAgenda() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      const start = startOfMonth(cursor).toISOString();
      const end = endOfMonth(addMonths(cursor, 1)).toISOString();
      const r = await API.get(`/agenda/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
      setEvents(r.data?.events || []);
    } catch { /* silent fail — geen blocker voor mail-hub */ }
  }, [cursor]);

  useEffect(() => { load(); }, [load]);

  const grid = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = []; let d = gStart;
    while (d <= gEnd) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [cursor]);

  const today = new Date();
  const upcoming = useMemo(() => {
    return events
      .filter(e => isAfter(safeParse(e.eind), today))
      .sort((a, b) => safeParse(a.start) - safeParse(b.start))
      .slice(0, 5);
  }, [events]);

  const eventsByDay = useMemo(() => {
    const m = new Map();
    events.forEach(e => {
      const k = format(safeParse(e.start), 'yyyy-MM-dd');
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(e);
    });
    return m;
  }, [events]);

  const goAgenda = (d = null) => {
    navigate('/agenda' + (d ? `?d=${format(d, 'yyyy-MM-dd')}` : ''));
  };

  const dayHeaders = ['M', 'D', 'W', 'D', 'V', 'Z', 'Z'];

  return (
    <div className="border-t mt-3 pt-2" data-testid="mail-mini-agenda">
      <div className="flex items-center justify-between px-2 mb-1">
        <button
          onClick={() => goAgenda()}
          className="text-[10px] font-bold uppercase text-gray-500 hover:text-[#002FA7] flex items-center gap-1"
          title="Open volledige agenda"
          data-testid="mail-mini-agenda-open"
        >
          <CalendarBlank size={11} weight="duotone" /> Agenda
        </button>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setCursor(d => subMonths(d, 1))} className="p-0.5 hover:bg-gray-200 rounded">
            <CaretLeft size={11} />
          </button>
          <span className="text-[10px] font-semibold text-gray-700 capitalize w-20 text-center">
            {format(cursor, 'MMM yyyy', { locale: nl })}
          </span>
          <button onClick={() => setCursor(d => addMonths(d, 1))} className="p-0.5 hover:bg-gray-200 rounded">
            <CaretRight size={11} />
          </button>
        </div>
      </div>

      {/* Mini-kalender grid */}
      <div className="px-1">
        <div className="grid grid-cols-7 gap-0.5 mb-0.5">
          {dayHeaders.map((h, i) => (
            <div key={i} className="text-center text-[9px] text-gray-400 font-bold">{h}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {grid.map((d, i) => {
            const isInMonth = isSameMonth(d, cursor);
            const isToday = isSameDay(d, today);
            const dayEvents = eventsByDay.get(format(d, 'yyyy-MM-dd')) || [];
            const hasEvents = dayEvents.length > 0;
            return (
              <button
                key={i}
                onClick={() => goAgenda(d)}
                className={`relative text-[10px] aspect-square rounded transition flex items-center justify-center
                  ${isToday ? 'bg-[#002FA7] text-white font-bold' :
                    hasEvents ? 'bg-violet-50 hover:bg-violet-100' :
                    isInMonth ? 'text-gray-700 hover:bg-gray-100' :
                    'text-gray-300 hover:bg-gray-50'}`}
                title={hasEvents ? `${dayEvents.length} event(s)` : ''}
              >
                {format(d, 'd')}
                {hasEvents && !isToday && (
                  <span
                    className="absolute bottom-0.5 right-0.5 w-1 h-1 rounded-full"
                    style={{ background: dayEvents[0].kleur || '#8B5CF6' }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Komende events */}
      <div className="px-2 mt-2">
        <div className="text-[9px] font-bold uppercase text-gray-400 mb-1">Komende</div>
        {upcoming.length === 0 ? (
          <div className="text-[10px] text-gray-400 italic py-1">Geen events</div>
        ) : (
          <div className="space-y-1">
            {upcoming.map(e => {
              const s = safeParse(e.start);
              const isVandaag = isSameDay(s, today);
              return (
                <button
                  key={e.id}
                  onClick={() => goAgenda(s)}
                  className="w-full text-left flex items-start gap-1.5 px-1.5 py-1 rounded hover:bg-gray-100 transition"
                  title={`${e.titel}${e.locatie ? ' • ' + e.locatie : ''}`}
                >
                  <span
                    className="w-1 self-stretch rounded flex-shrink-0 mt-0.5"
                    style={{ background: e.kleur || '#8B5CF6' }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[10px] text-gray-500">
                      {isVandaag ? 'Vandaag' : format(s, 'd MMM', { locale: nl })}
                      {!e.all_day && ` • ${format(s, 'HH:mm')}`}
                    </span>
                    <span className="block text-[11px] font-medium text-gray-800 truncate">{e.titel}</span>
                    {e.locatie && (
                      <span className="block text-[9px] text-gray-400 truncate flex items-center gap-0.5">
                        <MapPin size={8} /> {e.locatie}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
